import React, { useEffect, useState} from 'react';
import axios from 'axios';
import { TreeItem } from '@mui/x-tree-view';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import CustomTextField from "./CustomTextField.tsx";
import {DefaultButton} from "./Buttons.tsx";

interface customCategoryTreeProps {
  singleSelection?:boolean;
  selectedItems?: Map<number, string>;
  onSelectedItemsChange?: (selectedItems: Map<number, string>) => void;
  tiefeLaengeMap?: Map<number, string>;
  ottoMap?: Map<number, { id: number; name: string }>;
  ebayMap?: Map<number, { id1: number; id2: number | null }>;
  hoodMap?: Map<number, { id: number }>;
}

// Tree item: id, path, name
const CategoryTreeAddRemove : React.FC<customCategoryTreeProps> = (
  {
    singleSelection=true,
    selectedItems,
    onSelectedItemsChange,
    tiefeLaengeMap,
    ottoMap,
    ebayMap,
    hoodMap,
    ...props
  }) => {
  const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL;

  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      };
      await axios.get(`${backendUrl}/general/get_categories/`, config)
        .then(response => {
          const treeData = buildTree(response.data);
          setCategories(treeData);
          setFilteredCategories(treeData);
        })
        .catch(error => alert("Fehler beim Laden der Kategorien " + error.toString()))
    };
    fetchData();
  }, []);

  // Gets a flat list of categories and builds the tree-structure that can be used by the TreeView
  const buildTree = (categories) => {
    const map = {};
    categories.forEach(cat => map[cat.path] = { ...cat, children: [] }); // Store each category in cat.path
    // use ...cat to also store id, name and path
    const tree = [];
    // Add categories to the tree
    categories.forEach(cat => {
      const parentPath = cat.path.split('.').slice(0, -1).join('.'); // Get parent path
      if (parentPath && map[parentPath]) { // parent path exists and is in the map (indicating it is a child of another category)
        map[parentPath].children.push(map[cat.path]);
      } else { // parent path does not exist (Because cat is the highest possible category (e.g. Wohnzimmer))
        tree.push(map[cat.path]);
      }
    });
    return tree;
  };

  // Method for filtering items from the tree with a search box
  const filterTree = (nodes, term) => {
    return nodes // Return filtered categories with the help of map
      .map((node) => {
        // Ensure children exists
        const children = node.children || [];

        // Check if the current node matches
        const isMatch = node.name.toLowerCase().includes(term.toLowerCase());

        // If this node matches, return it with ALL of its children.
        // This ensures the following behaviour: If I type "Wohnzimmer" I want to get all subcategories of "Wohnzimmer"
        if (isMatch) {
          return { ...node, children};
        }
        // Recursively filter children
        const filteredChildren = filterTree(children, term);

        // If node matches or any of its children match, keep it
        if (isMatch || filteredChildren.length > 0) {
          return { ...node, children: filteredChildren };
        }

        return null
      })
      .filter((node) => node !== null);
  };
  // Hook to search if the searchTerm or categories are changed
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredCategories(categories);
      setExpanded([]);
    } else {
      const f = filterTree(categories, searchTerm);
      setFilteredCategories(f);
      setExpanded(getAllPaths(f)); // Expand all found items
    }
  }, [searchTerm, categories]);




  // Method to get all the nodes' paths in a list. This is for setting the expanded state of all of them
  const getAllPaths = (nodes) => {
      return nodes.flatMap( node => [
        node.path, ...(node.children ? getAllPaths(node.children) : [])
      ]);
    };


  const handleSelectionChange = (path: string, id: number) => {
    const newSelectedItems = singleSelection ? new Map() : new Map(selectedItems);

    if (singleSelection) {
      // Single selection: replace the entire selection with the new one
      newSelectedItems.set(id, path);
    } else {
      // Multi-selection: toggle selection
      if (newSelectedItems.has(id)) {
        newSelectedItems.delete(id);
      } else {
        newSelectedItems.set(id, path);
      }
    }

    onSelectedItemsChange?.(newSelectedItems);
  };

  const handleExpandClick = () => {
    if (expanded.length === 0) {
      setExpanded(getAllPaths(categories));
    }
    else {
      setExpanded([]);
    }
  };


  const renderTree = (nodes) => (
    nodes.map(node => {
      const shopTag = node.shop_id === 73461 ? "Gartengarten24" : "Natur24";
      const tagColor = node.shop_id === 73461 ? "#e6b800" : "var(--naturGreen)"; // Gold/Yellow for GG24, Green for N24

      const massTag = tiefeLaengeMap?.get(node.id);
      const massColor = massTag === "Laenge" ? "#1976d2" : "#9c27b0";

      return (
        <TreeItem key={node.id}
          itemId={node.path}
          // label={node.name}
          label={
            <div
              style={{display: 'flex', alignItems: 'center', gap: '25px'}}
              // Only select if singleSelection is set to true
              onClick={() => handleSelectionChange(node.path, node.id)}
            >
              <span>{node.name}</span>
              <span style={{
                backgroundColor: tagColor,
                color: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                lineHeight: '1'
              }}>
                {shopTag}
              </span>
              {massTag && (
                <span style={{ backgroundColor: massColor, color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem' }}>
                  {massTag === "Laenge" ? "Länge" : "Tiefe"}
                </span>
              )}
              {ottoMap?.has(node.id) && (
                <span style={{ backgroundColor: '#E50010', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem' }}>
                  Otto: {ottoMap.get(node.id)!.id} ({ottoMap.get(node.id)!.name})
                </span>
              )}
              {ebayMap?.has(node.id) && (
                <span style={{ backgroundColor: '#0064D2', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem' }}>
                  eBay: {ebayMap.get(node.id)!.id1} {ebayMap.get(node.id)!.id2 ? `/ ${ebayMap.get(node.id)!.id2}` : ''}
                </span>
              )}
              {hoodMap?.has(node.id) && (
                <span style={{ backgroundColor: '#FF6600', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem' }}>
                  Hood: {hoodMap.get(node.id)!.id}
                </span>
              )}
            </div>
          }
          // onClick={() => handleCheckboxChange(node.path, node.id, !selectedItems.has(node.id))}
        >
          {node.children.length > 0 && renderTree(node.children)}
        </TreeItem>
      );
    })
  );

  return (
    <div style={{flexDirection: 'column', alignContent: 'start', justifyContent: 'center'}}>
      <div style={{
        display: 'flex',
        flexDirection: 'row', alignItems: 'center', columnGap: '15px', justifyContent: 'center', width: "100%", minWidth: "100%", marginBottom: "15px"}}>
        <CustomTextField label="Suchbegriff"
                       placeholder="Suchbegriff"
                       value={searchTerm}
                       onChange={(e) => setSearchTerm(e.target.value)}
                       addEraseButton={true}
                       sx = {{ width: "60%", minWidth: "60%", height: "100%"}}
        />
        <DefaultButton label={expanded.length === 0 ? "Alle ausklappen" : "Alle einklappen"}
                       onClick={handleExpandClick}
                       sx = {{ width: "175px", minWidth: "35%"}}
        />
      </div>
      <SimpleTreeView
        expandedItems={expanded}
        onExpandedItemsChange={(_, items) => setExpanded(items)}
        checkboxSelection={false}
        multiSelect={!singleSelection}
        selectedItems={singleSelection
          ? (Array.from(selectedItems?.values() || [])[0] || "")
          : Array.from(selectedItems?.values() || [])}
        // onSelectedItemsChange={(_, selectedPaths) => {
        //   const newSelectedItems = new Map(selectedItems);
        //   onSelectedItemsChange(newSelectedItems)
        // }}
        disableSelection={true}
        expansionTrigger="iconContainer"
        sx = {{ width: "100%",
                '& .MuiTreeItem-content': {
                  minHeight: '30px',
                  maxHeight: '30px',
                  padding: '15px 15px',    // reduce left/right padding
                },
                // 3) Adjust the icon container (expand/collapse arrow)
                '& .MuiTreeItem-iconContainer': {
                  width: '15px',       // smaller width
                  marginRight: '2px',  // less spacing before label
                },
                "& .MuiTreeItem-label": {
                  textAlign: "left", // Align text to the left
                  justifyContent: "flex-start", // Ensure content aligns left
                },
                "& .MuiTreeItem-root": {
                  color: "var(--font-primary)",
                },
                "& .MuiTreeItem-content:hover": {
                  backgroundColor: "var(--naturGreenAlpha05) !important", // Light green hover
                },
                "& .MuiTreeItem-content.Mui-selected": {
                  backgroundColor: "var(--naturGreenAlpha05) !important",
                  color: "white",
                },
                "& .MuiTreeItem-content.Mui-selected:hover": {
                  backgroundColor: "var(--naturGreenAlpha05) !important",
                },
                "& .MuiCheckbox-root svg": {
                  color: "var(--font-primary)", // Default color (when not selected)
                },
                "& .MuiCheckbox-root.Mui-checked svg": {
                  color: "var(--naturGreen) !important", // Color when selected
                },
        }}
        {...props}
      >
        {renderTree(filteredCategories)}
      </SimpleTreeView>
    </div>
  );
};

export default CategoryTreeAddRemove;
